import { api } from './api.ts';

// Web Push from the browser's side. iPhone and iPad only allow it once the app is on the Home Screen (iOS 16.4+).
export type PushState = 'unsupported' | 'needs-install' | 'denied' | 'on' | 'off' | 'not-configured';

export const standalone = () => matchMedia('(display-mode: standalone)').matches || (navigator as any).standalone === true;
export const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
const supported = () => 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

export async function pushState(): Promise<PushState> {
  if (!supported()) return isIOS() && !standalone() ? 'needs-install' : 'unsupported';
  if (Notification.permission === 'denied') return 'denied';
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (sub && Notification.permission === 'granted') return 'on';
  const { configured } = await api<{ configured: boolean }>('/push');
  return configured ? 'off' : 'not-configured';
}

const fromB64 = (s: string) => Uint8Array.from(atob((s + '='.repeat((4 - (s.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/')), (c) => c.charCodeAt(0));

// Must run from a tap: browsers only show the permission prompt for a user gesture.
export async function enablePush() {
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') return pushState();
  const { key } = await api<{ key: string }>('/push');
  const reg = await navigator.serviceWorker.ready;
  const sub = (await reg.pushManager.getSubscription()) ?? (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: fromB64(key) }));
  await api('/push/subscribe', { method: 'POST', body: sub.toJSON() });
  await api('/push/test', { method: 'POST' });
  return 'on' as const;
}

export async function disablePush() {
  const sub = await (await navigator.serviceWorker.ready).pushManager.getSubscription();
  if (sub) { await api('/push/unsubscribe', { method: 'POST', body: { endpoint: sub.endpoint } }); await sub.unsubscribe(); }
  return 'off' as const;
}
